import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ClipboardList, Loader2, ArrowUpRight, RefreshCw, Download, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../ui/dialog';
import { ColorImage } from '../ColorImage';
import { StockDot } from '../../../fabric/StockDot';
import { STOCK_OPTIONS, DISCONTINUED, stockBadge, tierLabels } from '../../../../lib/fabricLabels';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ATTENTION = ['limited', 'not_in_stock', 'discontinued'];

const matches = (r, statuses) => statuses.includes(r.status) || (statuses.includes('discontinued') && r.discontinued);

const csvEscape = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const buildStockCsv = (rows) => {
  const header = ['Brand', 'Type / Collection', 'Collection / Material', 'Color', 'Article code', 'Stock status', 'Discontinued', 'Status set on', 'Level'];
  const lines = rows.map((r) => [
    r.brand_name, r.collection_name, r.material_name || '', r.level === 'color' ? r.name : '', r.code || '',
    stockBadge(r.status)?.label || '', r.discontinued ? 'Yes' : 'No',
    r.source === 'own' ? (r.level === 'color' ? 'color' : r.level) : r.source, r.level,
  ].map(csvEscape).join(','));
  return [header.join(','), ...lines].join('\n');
};

export const StockReportDialog = ({ open, onOpenChange, token, onJump }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [statuses, setStatuses] = useState(ATTENTION);
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/fabric-collections/stock-report`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.rows || []).filter((r) => matches(r, statuses) && (!needle || [r.name, r.code, r.collection_name, r.material_name, r.brand_name].some((v) => v?.toLowerCase().includes(needle))));
  }, [data, statuses, q]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const r of visibleRows) {
      const key = `${r.collection_id}:${r.material_id || ''}`;
      if (!map.has(key)) map.set(key, { key, brand_name: r.brand_name, collection_id: r.collection_id, collection_name: r.collection_name, material_id: r.material_id, material_name: r.material_name, header: null, colors: [] });
      const g = map.get(key);
      if (r.level === 'color') g.colors.push(r);
      else if (r.level === 'material' || (r.level === 'collection' && !r.material_id)) g.header = g.header || r;
    }
    return [...map.values()].filter((g) => g.colors.length || g.header).sort((a, b) => `${a.brand_name}${a.collection_name}${a.material_name || ''}`.localeCompare(`${b.brand_name}${b.collection_name}${b.material_name || ''}`));
  }, [visibleRows]);

  const exportCsv = () => {
    const rows = visibleRows.filter((r) => r.level === 'color');
    if (!rows.length) { toast.error('Nothing to export in this view'); return; }
    const blob = new Blob(['\ufeff' + buildStockCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement('a'), { href: url, download: `stock-overview-${new Date().toISOString().slice(0, 10)}.csv` });
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} colors`);
  };

  const toggle = (v) => setStatuses((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  const totals = data?.totals || {};
  const chipCls = (on) => `inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-xs transition-colors ${on ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400'}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[88vh] flex flex-col" data-testid="stock-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Stock overview</DialogTitle>
          <DialogDescription>Every collection and color with a stock status or discontinued flag, grouped by where it sits. Inherited statuses are marked.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          {STOCK_OPTIONS.map((o) => (
            <button key={o.value} type="button" onClick={() => toggle(o.value)} className={chipCls(statuses.includes(o.value))} data-testid={`stock-report-filter-${o.value}`}>
              <span className={`w-2 h-2 rounded-full ${o.dot}`} />
              {o.label.replace(' available', '')}
              <span className="opacity-60">{totals[o.value] || 0}</span>
            </button>
          ))}
          <button type="button" onClick={() => toggle('discontinued')} className={chipCls(statuses.includes('discontinued'))} data-testid="stock-report-filter-discontinued">
            <AlertCircle className="w-3 h-3 text-red-500" strokeWidth={2.5} />
            Discontinued
            <span className="opacity-60">{totals.discontinued || 0}</span>
          </button>
          <div className="flex-1" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name, code, collection…" className="h-8 text-xs w-56" data-testid="stock-report-search" />
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="h-8 gap-1" title="Refresh"><RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /></Button>
          <Button size="sm" onClick={exportCsv} disabled={loading || !data} className="h-8 gap-1 bg-amber-600 hover:bg-amber-700" data-testid="stock-report-export-btn">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 pt-2" data-testid="stock-report-list">
          {loading && !data ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-neutral-400" /></div>
          ) : groups.length === 0 ? (
            <p className="py-12 text-center text-sm text-neutral-500" data-testid="stock-report-empty">Nothing matches — every fabric in this view is available.</p>
          ) : groups.map((g) => {
            const L = tierLabels(g.brand_name);
            const header = g.header;
            const headerText = header ? [stockBadge(header.status)?.label, header.discontinued ? DISCONTINUED.label : null].filter(Boolean).join(' · ') : '';
            return (
              <div key={g.key} className="mb-4 rounded-lg border border-neutral-200 overflow-hidden" data-testid={`stock-report-group-${g.material_id || g.collection_id}`}>
                <div className="flex items-center gap-2 px-3 py-2 bg-neutral-50 border-b border-neutral-200">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-neutral-500 truncate">{g.brand_name} › {g.collection_name}{g.material_name ? '' : ` (${L.tier2})`}</p>
                    <p className="text-sm font-semibold text-neutral-900 truncate flex items-center gap-2">
                      {g.material_name || g.collection_name}
                      {header && <StockDot status={header.status} discontinued={header.discontinued} testId={`stock-report-header-dot-${g.material_id || g.collection_id}`} side="bottom" />}
                      {header && <span className="text-[10px] font-normal text-neutral-500">whole {g.material_name ? L.tier3.toLowerCase() : L.tier2.toLowerCase()}: {headerText}</span>}
                    </p>
                  </div>
                  <span className="text-xs text-neutral-500">{g.colors.length} color{g.colors.length === 1 ? '' : 's'}</span>
                  <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => onJump(g)} data-testid={`stock-report-jump-${g.material_id || g.collection_id}`}>
                    Open <ArrowUpRight className="w-3 h-3" />
                  </Button>
                </div>
                {g.colors.length > 0 && (
                  <ul className="divide-y divide-neutral-100">
                    {g.colors.map((c) => (
                      <li key={c.color_id} className="flex items-center gap-3 px-3 py-1.5" data-testid={`stock-report-row-${c.color_id}`}>
                        <div className="w-7 h-7 rounded border border-neutral-200 overflow-hidden flex-shrink-0">
                          {c.image_url ? <ColorImage src={c.image_url} alt="" w={128} className="w-full h-full" /> : <div className="w-full h-full" style={{ backgroundColor: c.hex_color || '#e5e5e5' }} />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-neutral-900 truncate">{c.name}</p>
                          {c.code && <p className="text-[10px] text-neutral-500 truncate">{c.code}</p>}
                        </div>
                        <span className="text-[10px] text-neutral-400 hidden sm:inline">{c.source === 'own' ? 'set on color' : `inherited from ${c.source === 'material' ? L.tier3.toLowerCase() : L.tier2.toLowerCase()}`}</span>
                        <span className="inline-flex items-center gap-1.5 text-[11px] text-neutral-700 w-56 justify-end text-right">
                          {[stockBadge(c.status)?.label, c.discontinued ? DISCONTINUED.label : null].filter(Boolean).join(' · ')}
                          <StockDot status={c.status} discontinued={c.discontinued} testId={`stock-report-dot-${c.color_id}`} />
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StockReportDialog;
